const REMATCH_API_ROOT = '/api/rematch';
// Leave enough time for the bridge's 15-second upstream timeout to return its
// structured gateway response before the browser aborts the same request.
export const REMATCH_REQUEST_TIMEOUT_MS = 20 * 1000;
// A handover may include Sharetribe completion verification, the ReMatch write,
// and donation listing closure before the proxy can return its final response.
export const REMATCH_HANDOVER_REQUEST_TIMEOUT_MS = 45 * 1000;

const readResponse = async response => {
  if (response.status === 204) {
    return null;
  }

  const text = await response.text();
  if (!text) {
    return null;
  }

  const contentType = response.headers.get('Content-Type') || '';
  if (contentType.toLowerCase().includes('application/json')) {
    try {
      return JSON.parse(text);
    } catch (e) {
      return text;
    }
  }

  return text;
};

const errorMessage = (body, fallback) => {
  if (typeof body === 'string' && body.trim()) {
    return body;
  }
  if (Array.isArray(body?.message)) {
    return body.message.join(' ');
  }
  return body?.message || body?.error || fallback;
};

export class RematchApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'RematchApiError';
    this.status = status;
    this.body = body;
  }
}

export const isAmbiguousRematchError = error =>
  typeof error?.status !== 'number' || error.status === 0 || error.status >= 500;

export const rematchRequest = async (path, options = {}) => {
  const { body, headers, signal, timeoutMs = REMATCH_REQUEST_TIMEOUT_MS, ...rest } = options;
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const abortForCaller = () => controller?.abort();

  if (controller && signal) {
    if (signal.aborted) {
      controller.abort();
    } else {
      signal.addEventListener('abort', abortForCaller, { once: true });
    }
  }

  let timedOut = false;
  const timeoutId = controller
    ? setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, timeoutMs)
    : null;

  try {
    const response = await fetch(`${REMATCH_API_ROOT}${path}`, {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...headers,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
      ...rest,
      signal: controller?.signal || signal,
    });
    const responseBody = await readResponse(response);

    if (!response.ok) {
      throw new RematchApiError(
        errorMessage(responseBody, 'The ReMatch request could not be completed.'),
        response.status,
        responseBody
      );
    }

    return responseBody;
  } catch (error) {
    if (timedOut) {
      throw new RematchApiError('The ReMatch request timed out.', 504, null);
    }
    throw error;
  } finally {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
    signal?.removeEventListener?.('abort', abortForCaller);
  }
};

export const fetchConsumerMe = (options = {}) => rematchRequest('/me', options);

export const fetchConsumerConsents = (options = {}) => rematchRequest('/consents', options);

export const updateConsumerConsent = ({ purpose, granted, source }, options = {}) =>
  rematchRequest('/consents', {
    ...options,
    method: 'POST',
    body: { purpose, granted, source },
  });

export const fetchConsumerPoints = (options = {}) => rematchRequest('/points', options);

export const fetchConsumerImpact = (options = {}) => rematchRequest('/impact', options);

export const confirmConsumerHandover = (
  { sharetribeListingId, sharetribeTransactionId, confirmedAt, geo },
  options = {}
) =>
  rematchRequest('/handovers', {
    ...options,
    method: 'POST',
    timeoutMs: REMATCH_HANDOVER_REQUEST_TIMEOUT_MS,
    body: {
      ...(sharetribeListingId ? { sharetribeListingId } : {}),
      ...(sharetribeTransactionId ? { sharetribeTransactionId } : {}),
      ...(confirmedAt ? { confirmedAt } : {}),
      ...(geo ? { geo } : {}),
    },
  });

export const fetchRewardOffers = (options = {}) => rematchRequest('/rewards/offers', options);

export const fetchRewardRedemptions = (options = {}) =>
  rematchRequest('/rewards/redemptions', options);

export const redeemRewardOffer = (offerUuid, requestUuid, options = {}) =>
  rematchRequest('/rewards/redemptions', {
    ...options,
    method: 'POST',
    body: { offerUuid, requestUuid },
  });

export const fetchSurveyResponses = (options = {}) => rematchRequest('/surveys/responses', options);

export const submitSurveyResponse = ({ redemptionUuid, surveyKey, answers }, options = {}) =>
  rematchRequest('/surveys/responses', {
    ...options,
    method: 'POST',
    body: { redemptionUuid, surveyKey, answers },
  });
